import { normalizeLedgerFields } from "./ledger-field-compat";
import type { Owner } from "../types";
import type { ReceiptDraft } from "../types/receipt";
import { deleteOfflineBlobs, getOfflineBlob } from "./offline-blobs";
import { imageWithThumbnail } from "./image-data";
import { PENDING_CHANGED, pendingChanged } from "./pending-entries";
import {
  categoryNeedsFromExpense,
  ensureCategoriesForSync,
  prepareExpenseForSync,
} from "./offline-associated";

export const RECEIPT_PREFIX = "barnyard-pending-receipt-";

export type PendingReceiptScan = {
  kind: "scan";
  id: string;
  owner: Owner;
  attachments: { id: string; sameAsPrevious: boolean; name: string; type: string }[];
};

export type PendingReceiptSave = {
  kind: "save";
  id: string;
  owner: Owner;
  draft: ReceiptDraft;
  trackingItemIds: string[];
  progress?: {
    transactionId?: string;
    attachedKeys?: string[];
    savedPriceIds?: string[];
  };
};

export type PendingReceipt = PendingReceiptScan | PendingReceiptSave;

export function readPendingReceipts(): { key: string; receipt: PendingReceipt }[] {
  const items: { key: string; receipt: PendingReceipt }[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key?.startsWith(RECEIPT_PREFIX)) continue;
    try {
      const receipt = normalizeLedgerFields(JSON.parse(localStorage.getItem(key) || "null")) as PendingReceipt | null;
      if (receipt?.id && (receipt.kind === "scan" || receipt.kind === "save"))
        items.push({ key, receipt });
    } catch { /* Ignore malformed records. */ }
  }
  return items;
}

export function enqueuePendingReceipt(receipt: PendingReceipt) {
  const key = RECEIPT_PREFIX + receipt.id;
  if (!localStorage.getItem(key)) localStorage.setItem(key, JSON.stringify(receipt));
  pendingChanged();
  return key;
}

export function removePendingReceipt(id: string) {
  localStorage.removeItem(RECEIPT_PREFIX + id);
  pendingChanged();
}

async function postJson(request: typeof fetch, path: string, body: unknown) {
  const response = await request(path, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    signal: AbortSignal.timeout(65000),
  });
  const value = await response.json() as { verified?: boolean; id?: string; error?: string; receipts?: ReceiptDraft[] };
  if (!response.ok) throw new Error(value.error || "Receipt sync failed.");
  return value;
}

async function syncScan(
  key: string,
  receipt: PendingReceiptScan,
  request: typeof fetch,
): Promise<void> {
  const files = [];
  for (const item of receipt.attachments) {
    const blob = await getOfflineBlob(item.id);
    if (!blob) throw new Error("A receipt photo is missing from this device. Capture it again.");
    files.push({
      sameAsPrevious: item.sameAsPrevious,
      file: new File([blob], item.name || "receipt.jpg", { type: item.type || blob.type || "image/jpeg" }),
    });
  }
  const groups = files.reduce<{ file: File }[][]>((acc, item, index) => {
    if (index && item.sameAsPrevious) acc.at(-1)!.push(item);
    else acc.push([item]);
    return acc;
  }, []);
  const imageGroups: string[][] = [];
  const thumbnailGroups: string[][] = [];
  for (const group of groups) {
    const done = await Promise.all(group.map((v) => imageWithThumbnail(v.file)));
    imageGroups.push(done.map((d) => d.image));
    thumbnailGroups.push(done.map((d) => d.thumbnail));
  }
  const scanned = await postJson(request, "/api/receipts/scan", { imageGroups, thumbnailGroups });
  const drafts = scanned.receipts || [];
  if (!drafts.length) throw new Error("The receipt could not be read. Try scanning again.");
  const [draft, ...remainingDrafts] = drafts;
  localStorage.setItem(`barnyard-receipt-${receipt.owner}`, JSON.stringify({
    draft,
    remainingDrafts,
    receiptNumber: 1,
    receiptTotal: drafts.length,
    step: "details",
    pending: false,
    updated: Date.now(),
    trackingItemIds: [],
  }));
  await deleteOfflineBlobs(receipt.attachments.map((item) => item.id));
  localStorage.removeItem(key);
  pendingChanged();
}

async function syncSave(
  key: string,
  receipt: PendingReceiptSave,
  request: typeof fetch,
): Promise<void> {
  const draft = {
    ...receipt.draft,
    expense: prepareExpenseForSync(receipt.draft.expense),
  };
  await ensureCategoriesForSync(categoryNeedsFromExpense(receipt.draft.expense), request);
  let progress = { ...(receipt.progress || {}) };
  if (!progress.transactionId) {
    const saved = await postJson(request, "/api/receipts/save", draft);
    if (!saved.verified || !saved.id) throw new Error(saved.error || "Receipt save was not confirmed.");
    progress = { ...progress, transactionId: saved.id, attachedKeys: progress.attachedKeys || [], savedPriceIds: progress.savedPriceIds || [] };
    localStorage.setItem(key, JSON.stringify({ ...receipt, progress }));
    pendingChanged();
  }
  const attached = new Set(progress.attachedKeys || []);
  for (const image of draft.pendingImages || []) {
    if (attached.has(image.key)) continue;
    await postJson(request, "/api/receipts/image", { receiptId: draft.id, ...image });
    attached.add(image.key);
    progress = { ...progress, attachedKeys: [...attached] };
    localStorage.setItem(key, JSON.stringify({ ...receipt, progress }));
    pendingChanged();
  }
  const priced = new Set(progress.savedPriceIds || []);
  for (const itemId of receipt.trackingItemIds) {
    if (priced.has(itemId)) continue;
    const item = draft.items.find((row) => row.id === itemId);
    if (!item) continue;
    const price = await postJson(request, "/api/prices", {
      receiptId: draft.id,
      item: { ...item, group: item.group || item.name },
    });
    if (!price.verified) throw new Error(price.error || "A receipt price still needs confirmation.");
    priced.add(itemId);
    progress = { ...progress, savedPriceIds: [...priced] };
    localStorage.setItem(key, JSON.stringify({ ...receipt, progress }));
    pendingChanged();
  }
  localStorage.removeItem(key);
  pendingChanged();
}

/** Finish queued receipt scans (into a review draft) and receipt saves (into the ledger). */
export async function syncPendingReceipts(request: typeof fetch = fetch): Promise<{ confirmed: number; errors: string[] }> {
  let confirmed = 0;
  const errors: string[] = [];
  for (const { key, receipt } of readPendingReceipts()) {
    if (typeof navigator !== "undefined" && !navigator.onLine) break;
    try {
      if (receipt.kind === "scan") {
        await syncScan(key, receipt, request);
        // Scan yields a review draft, not a confirmed ledger row.
      } else {
        await syncSave(key, receipt, request);
        confirmed++;
      }
    } catch (error) {
      errors.push(error instanceof Error ? error.message : "A receipt is still waiting to sync.");
    }
  }
  return { confirmed, errors };
}

export { PENDING_CHANGED };
